import React, { useEffect, useState } from "react";

/**
 * PhotoLightbox — simple full-screen viewer for a list of photo URLs
 * attached to a diary. Receives a `photos` array of either strings (URLs)
 * or { url, caption } objects.
 */
export default function PhotoLightbox({ photos = [], title = "Photos", onClose }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, photos.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);

  if (!photos.length) {
    return (
      <div style={styles.backdrop} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>{title}</h2>
            <button style={styles.closeButton} onClick={onClose}>×</button>
          </div>
          <div style={styles.empty}>No photos attached to this diary.</div>
        </div>
      </div>
    );
  }

  const current = photos[index];
  const url = typeof current === "string" ? current : current?.url;
  const caption = typeof current === "string" ? "" : (current?.caption || "");

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{title} ({index + 1}/{photos.length})</h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.stage}>
          <button
            style={{ ...styles.navButton, left: 12 }}
            onClick={() => setIndex((i) => Math.max(i - 1, 0))}
            disabled={index === 0}
          >‹</button>

          <img src={url} alt={caption || `Photo ${index + 1}`} style={styles.image} />

          <button
            style={{ ...styles.navButton, right: 12 }}
            onClick={() => setIndex((i) => Math.min(i + 1, photos.length - 1))}
            disabled={index === photos.length - 1}
          >›</button>
        </div>

        {caption && <div style={styles.caption}>{caption}</div>}

        <div style={styles.thumbnails}>
          {photos.map((p, i) => {
            const u = typeof p === "string" ? p : p?.url;
            return (
              <button
                key={i}
                onClick={() => setIndex(i)}
                style={{
                  ...styles.thumb,
                  outline: i === index ? "3px solid #0ea5e9" : "1px solid #e2e8f0",
                }}
              >
                <img src={u} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.92)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1100, padding: 20,
  },
  modal: {
    width: "100%", maxWidth: 1100, maxHeight: "94vh",
    background: "#0f172a", borderRadius: 20,
    display: "grid", gridTemplateRows: "auto 1fr auto auto",
    overflow: "hidden", border: "1px solid #1e293b",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 18px", color: "#ffffff",
    background: "rgba(255,255,255,0.05)",
  },
  title: { margin: 0, fontSize: 14, fontWeight: 700 },
  closeButton: {
    border: "none", background: "rgba(255,255,255,0.15)",
    color: "#ffffff", fontSize: 22, lineHeight: 1,
    width: 32, height: 32, borderRadius: 8, cursor: "pointer",
  },
  stage: {
    position: "relative",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "#000",
    minHeight: 360,
    overflow: "hidden",
  },
  image: { maxWidth: "100%", maxHeight: "65vh", objectFit: "contain", borderRadius: 4 },
  navButton: {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: 44, height: 44, borderRadius: "50%",
    background: "rgba(255,255,255,0.15)",
    border: "none", color: "#ffffff", fontSize: 28, lineHeight: 1,
    cursor: "pointer", fontWeight: 700,
  },
  caption: { padding: "10px 18px", color: "#cbd5e1", fontSize: 12, fontStyle: "italic", background: "rgba(255,255,255,0.05)" },
  thumbnails: {
    display: "flex", gap: 6, padding: 12, overflowX: "auto",
    background: "rgba(255,255,255,0.03)",
  },
  thumb: {
    width: 64, height: 48,
    border: "none", padding: 0, borderRadius: 4,
    cursor: "pointer", flexShrink: 0,
    overflow: "hidden",
  },
  empty: { padding: 40, color: "#94a3b8", textAlign: "center" },
};
